import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractFormComponent } from '../../../../../shared/crud/component/form.component';
import { SolvimonEventForm } from './solvimon-event.form';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

interface IDefinition {
  event: string;
  metric: { name: string, description: string };
  values?: { type: string, name: string }[];
  properties?: { name: string, type: string, string?: string, enum?: { name: string }[] }[];
}

interface IDefinitions {
  [name: string]: IDefinition;
}

interface IMetricConfig {
  metric: { id: string, reference: string, name: string };
  values?: { id: string, reference: string, type: string, name: string }[];
  properties?: { id: string, reference: string, name: string, type: string, string?: string, enum?: { name: string, alias: string }[] }[];
}

@Component({
  selector: 'admin-solvimon-event-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class AdminSolvimonEventFormComponent extends AbstractFormComponent implements OnInit, AfterViewInit {

  @Input() row: any;
  @Input() definitions$: BehaviorSubject<{}>;
  @Output() makeDefault = new EventEmitter();
  @Output() delete = new EventEmitter();
  isEditMode: boolean = false;
  definitions: IDefinitions;


  constructor(private fb: FormBuilder,
              private cdr: ChangeDetectorRef,
              formBuilder: SolvimonEventForm,
              httpClient: HttpClient,
              toastrService: ToastrService,
              router: Router,
              route: ActivatedRoute) {
    super(formBuilder, httpClient, toastrService, router, route);
  }

  ngOnInit() {

    super.ngOnInit();
    this.definitions$.subscribe(d => {
      this.definitions = d;

      if (!this.row.event) {
        this.row.event = this.definitions[this.row.definition].event;
      }

      this.isEditMode = this.row.__editing;

      this.appendFormFields();

      this.record = this.row;
    });

  }

  ngAfterViewInit() {
    if (this.row.id) {
      this.formMode = 'update';
      this.form.reset(this.row);
    } else {
      this.formMode = 'create';
    }
    this.patchForm();
    this.cdr.detectChanges();
  }

  protected getUrl(forceId?: number): string {
    const params = this.route.snapshot.params;
    return '/admin/solvimon-event' + (params.id ? `/${params.id}` : forceId ? `/${forceId}` : '');
  }

  protected onSubmit(response: any): void {
    this.editRow();
  }

  submit() {
    super.submit();
  }

  getFormData(): any {
    return {
      name: this.form.controls.name.value,
      event: this.form.controls.event.value,
      definition: this.form.controls.definition.value,
      isActive: this.form.controls.isActive.value,
      config: this.configFromForm(),
    };
  }

  editRow() {
    this.isEditMode = !this.isEditMode;
  }

  configFromForm() {
    const definition = this.definitions[this.row.definition];

    const config: IMetricConfig = {
      metric: {
        id: this.form.get('metricId').value,
        reference: this.form.get('metricRef').value,
        name: definition.metric.name
      },
      properties: [],
      values: [],
    };

    if (definition.values) {
      definition.values.forEach((value, index) => {
        config.values.push({
          type: value.type,
          name: value.name,
          reference: this.form.get('values.' + index + ':' + value.name + ':ref').value,
          id: this.form.get('values.' + index + ':' + value.name + ':id').value,
        });
      });

    }

    if (definition.properties) {
      definition.properties.forEach((property, index) => {
        const configProperty = {
          id: this.form.get('properties.' + index + ':' + property.name + ':id').value,
          reference: this.form.get('properties.' + index + ':' + property.name + ':ref').value,
          name: property.name,
          type: property.type,
          enum: [],
        };
        if (property.type === 'enum') {
          property.enum.forEach((en, _) => {
            configProperty.enum.push({
              name: en.name,
              alias: this.form.get('properties.' + index + ':' + property.name + ':enum:' + en.name).value,
            });
          });
        }

        config.properties.push(configProperty);
      });
    }

    return config;
  }

  patchForm() {
    let formValues = { ...this.row };

    if (this.row.config) {
      const config = this.row.config as IMetricConfig;
      const configForm: { [key: string]: any } = {
        metricId: '',
        metricRef: '',
        values: {},
        properties: {},
      };

      if (config.metric) {
        configForm.metricId = config.metric.id;
        configForm.metricRef = config.metric.reference;
      }

      if (config.values) {
        config.values.forEach((value, index) => {
          configForm.values[index + ':' + value.name + ':id'] = value.id;
          configForm.values[index + ':' + value.name + ':ref'] = value.reference;
        });
      }

      if (config.properties) {
        config.properties.forEach((property, index) => {
          configForm.properties[index + ':' + property.name + ':id'] = property.id;
          configForm.properties[index + ':' + property.name + ':ref'] = property.reference;
          if (property.type === 'enum') {
            property.enum.forEach((en, _) => {
              configForm.properties[index + ':' + property.name + ':enum:' + en.name] = en.alias;
            });
          }
        });
      }

      formValues = { ...this.row, ...configForm };
    }

    this.form.patchValue(formValues);

    if (formValues.properties) {
      for (const [name, value] of Object.entries(formValues.properties)) {
        this.form.get(`properties.${name}`).patchValue(value);
      }
    }

    if (formValues.values) {
      for (const [name, value] of Object.entries(formValues.values)) {
        this.form.get(`values.${name}`).patchValue(value);
      }
    }

  }


  get valuesFormGroup(): FormGroup {
    return this.form.get('values') as FormGroup;
  }

  get propertiesFormGroup(): FormGroup {
    return this.form.get('properties') as FormGroup;
  }

  appendFormFields() {
    const definition = this.definitions[this.row.definition];

    if (definition.values) {
      const valuesFields = this.form.get('values') as FormGroup;
      definition.values.forEach((value, index) => {
        valuesFields.addControl(index + ':' + value.name + ':id', this.fb.control('', Validators.required));
        valuesFields.addControl(index + ':' + value.name + ':ref', this.fb.control('', Validators.required));
      });
    }

    if (definition.properties) {
      const propertiesFields = this.form.get('properties') as FormGroup;
      definition.properties.forEach((property, index) => {
        propertiesFields.addControl(index + ':' + property.name + ':id', this.fb.control('', Validators.required));
        propertiesFields.addControl(index + ':' + property.name + ':ref', this.fb.control('', Validators.required));
        if (property.type === 'enum') {
          property.enum.forEach((en, _) => {
            propertiesFields.addControl(index + ':' + property.name + ':enum:' + en.name, this.fb.control('', Validators.required));
          });
        }
      });
    }
  }
}
